import { ApiManager } from './ApiManager';
import { CategoryPageDTO, CategorySearchHitDTO } from './models/CategoryDTO';
import { PostDTO, TagDTO } from './models/PostDTO';

/** Slug of the Infos page itself in the category structure (backend `content.structure.ROOT_SLUG`) */
export const INFO_ROOT_SLUG = 'infos';

export default class ApiContentManager {
  /**
   * One page of the categorised Infos structure. The root page also says which
   * variant of the Infos page this user gets (A/B test) and the topics recommended to her.
   */
  static async getCategory(slug: string): Promise<CategoryPageDTO> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.get(`/api/content/categories/${slug}/`);
    return response.data;
  }

  /** Keeps `slug` in (or drops it from) the user's favourites, listed on the Infos home. */
  static async setCategoryBookmark(slug: string, bookmarked: boolean): Promise<{ is_bookmarked: boolean }> {
    const apiManager = ApiManager.getInstance();

    const url = `/api/content/categories/${slug}/bookmark/`;
    const response = bookmarked ? await apiManager.post(url, {}) : await apiManager.delete(url);
    return response.data;
  }

  /** Categories whose name or synonyms match `query` ("sopk" finds SMOP). */
  static async searchCategories(query: string): Promise<CategorySearchHitDTO[]> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.get(`/api/content/categories/`, { params: { search: query } });
    return response.data;
  }

  static async getPosts(): Promise<PostDTO[]> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.get(`/api/content/posts/`);
    return response.data;
  }

  static async getTags(): Promise<TagDTO[]> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.get(`/api/content/tags/`);
    return response.data;
  }

  static async getPost(id: string): Promise<PostDTO> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.get(`/api/content/posts/${id}/`);
    return response.data;
  }

  static async searchPosts(query: string | undefined, tags: string | undefined): Promise<PostDTO[]> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.get(`/api/content/posts/`, {
      params: { search: query, tags },
    });
    return response.data;
  }

  /** The published posts among `ids`, in no particular order. */
  static async getPostsByIds(ids: number[]): Promise<PostDTO[]> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.get(`/api/content/posts/`, {
      params: { ids: ids.join(',') },
    });
    return response.data;
  }

  /**
   * How far down a post the user scrolled (0..1) and for how long it was open.
   * The backend keeps the maximum of what it receives, so this can be sent at
   * milestones and again when the post is closed.
   */
  static async sendReadProgress(id: string, progress: { scroll_depth: number; seconds: number }): Promise<void> {
    const apiManager = ApiManager.getInstance();

    await apiManager.post(`/api/content/posts/${id}/read-progress/`, progress);
  }

  static async getRecentlyReadPosts(): Promise<PostDTO[]> {
    const apiManager = ApiManager.getInstance();

    const response = await apiManager.get(`/api/content/posts/recently-read/`);
    return response.data;
  }
}
